import React, { useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { TextField, Button, Grid, Typography, Autocomplete, Chip } from "@mui/material";
import DashboardLayout from "common/LayoutContainers/DashboardLayout";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom"; 
import MDBox from "components/MDBox";
import { getLineDetails } from "store/slice/linesSlice";
import { linesActions } from "store/slice/linesSlice";
import { deleteLine } from "store/slice/linesSlice";
import { updateLineDetails } from "store/slice/linesSlice";

const validationSchema = Yup.object({
  name: Yup.string().label("Name").max(255).required(),
  games: Yup.array().label("Games").of(Yup.number()).min(1).required(),
  studio: Yup.number().label("Studio").required(),
});

function LinesEdit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const store = useSelector((store) => store.lines);
  const { lineId } = useParams();
  const gamesOptions = store.games?.map((g) => ({ label: g.name, value: g.id })) || [];
  const studiosOptions = store.studios?.map((g) => ({ label: g.name, value: g.id })) || [];

  const FIELD_SIZE = "medium";

  useEffect(() => {
    if (lineId) {
      dispatch(getLineDetails(lineId));
    }
  }, [lineId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(linesActions.reset());
    }
  }, [dispatch]);

  const details = store.lineDetails;
  const error = store.error;
  const success = store.success;

  useEffect(() => {
    if (success?.status && lineId === 'new') {
      navigate(`/lines/${details.id}`);
    }
  }, [success?.status, details?.id, lineId, navigate ]);

  const initialValues = {
    name: details?.name || "",
    games: details?.lineGames?.map(cur => cur.gameId) || [],
    studio: details?.studio?.id || 0
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    await dispatch(updateLineDetails({ lineId, updatedData: values }));
    setSubmitting(false);
  };

  const handleDelete = async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this line?");
    if (confirmDelete) {
      await dispatch(deleteLine(lineId));
      navigate("/lines");
    }
  };

  return (
    <DashboardLayout>
      <Typography variant="h4" gutterBottom>
        {lineId === 'new' ? 'Add ': 'Edit'} Line Details
      </Typography>

      <MDBox mb={5}>
        {success.status || error.status ? (
          <div
            style={{
              color: success.status ? "green" : "red",
              padding: "10px",
              backgroundColor: success.status ? "#d4edda" : "#f8d7da",
              borderRadius: "5px",
              border: `1px solid ${success.status ? "#c3e6cb" : "#f5c6cb"}`,
              marginBottom: "10px",
            }}
          >
            {success.status
              ? "Line details updated successfully!"
              : "Failed to update line details. Please try again."}
          </div>
        ) : (
          ""
        )}
      </MDBox>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, handleChange, handleBlur, touched, errors, isSubmitting, setFieldValue }) => {
          return ((
            <Form>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    size={FIELD_SIZE}
                    fullWidth
                    label="Line Name"
                    name="name"
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}
                    variant="outlined"
                  />
                </Grid>
  
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    multiple
                    options={gamesOptions}
                    value={gamesOptions.filter((g) => {
                      return values.games.includes(g.value)
                    })}
                    onChange={(event, newValue) => {
                      // newValue = array of selected option objects
                      setFieldValue("games", newValue.map((x) => x.value));
                    }}
                    renderTags={(value, getTagProps) => {
                      return value.map((option, index) => {
                        const {key: _key, ...restOptions} = getTagProps({ index });
                        return <Chip key={_key} label={option.label} size="small" {...restOptions} />
                      })
                    }
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size={FIELD_SIZE}
                        variant="outlined"
                        label="Select Games"
                        placeholder="Choose games"
                        error={touched.games && Boolean(errors.games)}
                        helperText={touched.games && errors.games}
                      />
                    )}
                  />
                </Grid>
                
                <Grid size={{ xs: 12 }}>
                  <Autocomplete
                    options={studiosOptions}
                    getOptionLabel={(o) => o?.label ?? ""}
                    isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
                    value={studiosOptions.find((o) => o.value === values.studio) ?? null}
                    onChange={(event, newValue) => {
                      setFieldValue("studio", newValue?.value ?? "");
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size={FIELD_SIZE}
                        variant="outlined"
                        label="Select Studio"
                        placeholder="Choose studio"
                        error={touched.studio && Boolean(errors.studio)}
                        helperText={touched.studio && errors.studio}
                      />
                    )}
                  />
                </Grid>

  
                <Grid size={{ xs: 12 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
  
                  {/* Delete Button */}
                  {lineId !== 'new' ? (
                    <Button
                      variant="text"
                      color="error"
                      style={{ marginLeft: "10px" }}
                      onClick={handleDelete}
                    >
                      Delete Line
                    </Button>
                  ) : (
                    ""
                  )}
                </Grid>
              </Grid>
            </Form>
          ))
        }}
      </Formik>
    </DashboardLayout>
  );
}

export default LinesEdit;
